import type { RouteLocationNormalizedLoaded } from 'vue-router';
import type { UseContentfulPageResponseModel } from '~/composables/useContentfulPage';
import { firstEntry } from '~/utils/firstEntry';
import { resolveContentfulPageRouteMode } from '~/utils/contentful/resolveContentfulPageRouteMode';

/**
 * True when the resolved CMS payload belongs to the given route. Guards
 * consumers that run in `router.afterEach` against a payload that still holds
 * the previous page (e.g. after an aborted fetch).
 *
 * @param collapseSlug - same slug normalisation as `useContentfulPage`
 *   (document/software center sub-routes resolve to their root page).
 */
export function pagePayloadMatchesRoute(
  payload: UseContentfulPageResponseModel | null | undefined,
  route: Pick<RouteLocationNormalizedLoaded, 'path' | 'params'>,
  collapseSlug: (slug: string) => string = (slug) => slug
): boolean {
  const page = payload?.page;
  if (!page) {
    return false;
  }

  const mode = resolveContentfulPageRouteMode(route);
  if (mode === false) {
    return false;
  }

  if (mode === 'id') {
    return page.sys?.id === firstEntry(route.params.id);
  }

  const requestedSlug = collapseSlug(
    [route.params.slug ?? []].flat().filter(Boolean).join('/')
  );

  // Homepage is resolved via GPS id, its page slug is not the (empty) route slug.
  if (requestedSlug === '') {
    return true;
  }

  return (
    (page.fields?.slug ?? '').toLowerCase() === requestedSlug.toLowerCase()
  );
}
